import { Controller } from "@hotwired/stimulus"

// Combobox: filter building-complex options locally and navigate on pick.
export default class extends Controller {
  static targets = ["query", "select", "list"]
  static values = { selectedLabel: String }

  connect() {
    this.activeIndex = -1
    this.allOptions = Array.from(this.selectTarget.options).map((option) => ({
      value: option.value,
      label: (option.dataset.label || option.textContent).trim()
    }))
    this._onDocPointer = this.onDocPointer.bind(this)
    document.addEventListener("pointerdown", this._onDocPointer)
  }

  disconnect() {
    document.removeEventListener("pointerdown", this._onDocPointer)
  }

  open() {
    this.queryTarget.select()
    this.renderList(this.queryTarget.value)
    this.showList()
  }

  filter() {
    this.renderList(this.queryTarget.value)
    this.showList()
  }

  blur() {
    // Delay so option click can fire first.
    window.setTimeout(() => {
      if (!this.element.contains(document.activeElement)) {
        this.resetQuery()
        this.hideList()
      }
    }, 120)
  }

  keydown(event) {
    if (!this.listVisible()) {
      if (event.key === "ArrowDown" || event.key === "Enter") {
        event.preventDefault()
        this.open()
      }
      return
    }

    const items = this.optionButtons()
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.setActive(Math.min(this.activeIndex + 1, items.length - 1))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.setActive(Math.max(this.activeIndex - 1, 0))
    } else if (event.key === "Enter") {
      event.preventDefault()
      const item = items[this.activeIndex] || items[0]
      if (item) this.pick(item.dataset.value, item.dataset.label)
    } else if (event.key === "Escape") {
      event.preventDefault()
      this.resetQuery()
      this.hideList()
      this.queryTarget.blur()
    }
  }

  pick(value, label) {
    if (!value) return
    this.queryTarget.value = label || ""
    this.hideList()
    if (value !== this.selectTarget.value) {
      window.Turbo.visit(value)
    }
  }

  onDocPointer(event) {
    if (!this.element.contains(event.target)) {
      this.hideList()
    }
  }

  renderList(rawQuery) {
    const q = (rawQuery || "").trim().toLowerCase()
    const selectedLabel = (this.selectedLabelValue || "").trim().toLowerCase()
    // While the field still shows the current selection, treat as "show all".
    const filterQ = q && q !== selectedLabel ? q : ""

    const matches = this.allOptions.filter((opt) => {
      if (!filterQ) return true
      return opt.label.toLowerCase().includes(filterQ)
    })

    this.listTarget.innerHTML = ""
    this.activeIndex = -1

    if (matches.length === 0) {
      const empty = document.createElement("li")
      empty.className = "task-v2-bc-picker__empty"
      empty.setAttribute("role", "presentation")
      empty.textContent = "—"
      this.listTarget.appendChild(empty)
      return
    }

    matches.forEach((opt, index) => {
      const li = document.createElement("li")
      li.setAttribute("role", "option")
      li.id = `task_v2_bc_option_${index}`

      const button = document.createElement("button")
      button.type = "button"
      button.className = "task-v2-bc-picker__option"
      button.dataset.value = opt.value
      button.dataset.label = opt.label
      button.textContent = opt.label
      if (opt.label === this.selectedLabelValue) {
        button.classList.add("is-selected")
        button.setAttribute("aria-selected", "true")
      } else {
        button.setAttribute("aria-selected", "false")
      }
      button.addEventListener("mousedown", (event) => {
        event.preventDefault()
        this.pick(opt.value, opt.label)
      })

      li.appendChild(button)
      this.listTarget.appendChild(li)
    })

    if (matches.length > 0) this.setActive(0)
  }

  optionButtons() {
    return Array.from(this.listTarget.querySelectorAll(".task-v2-bc-picker__option"))
  }

  setActive(index) {
    const items = this.optionButtons()
    if (items.length === 0) {
      this.activeIndex = -1
      return
    }
    this.activeIndex = index
    items.forEach((el, i) => {
      el.classList.toggle("is-active", i === index)
    })
    const active = items[index]
    if (active) {
      this.queryTarget.setAttribute("aria-activedescendant", active.parentElement.id)
      active.scrollIntoView({ block: "nearest" })
    }
  }

  showList() {
    this.listTarget.hidden = false
    this.queryTarget.setAttribute("aria-expanded", "true")
  }

  hideList() {
    this.listTarget.hidden = true
    this.queryTarget.setAttribute("aria-expanded", "false")
    this.queryTarget.removeAttribute("aria-activedescendant")
    this.activeIndex = -1
  }

  listVisible() {
    return !this.listTarget.hidden
  }

  resetQuery() {
    this.queryTarget.value = this.selectedLabelValue || ""
  }
}
